// Service configuration for different client keys
export interface ServiceConfig {
  serviceId: string;
  serviceName: string;
  primaryColor: string;
  logoUrl?: string;
  maxDistance?: number; // in kilometers
  defaultLocation?: { lat: number; lng: number };
  defaultZoom?: number; // Default zoom level for the map (1-20, where 1 is world view and 20 is building level)
  contactInfo?: {
    phone?: string;
    email?: string;
  };
  timezone?: string; // Optional: IANA timezone string
  communicationMethods?: string[]; // Available communication methods (e.g., ['voice', 'sms', 'whatsapp', 'email'])
  attributes?: {
    // Common attribute keys include:
    // - paymentTypes: Comma-separated list of payment options (e.g., 'cash,credit card,account')
    // - showComfortLevels: Controls visibility of comfort level selection (true/false)
    // - defaultCommunicationMethod: Default communication method (e.g., 'Email', 'SMS')
    // - communicationMethods: Available communication methods (e.g., 'voice,sms,whatsapp,email')
    // - timezone: The service's timezone (e.g., 'America/New_York', 'Europe/London')
    // - enableGlobalSearch: Controls visibility of a global search option (true/false)
    [key: string]: string | number | boolean | string[];
  };
}

// Map of service keys to their configurations
export const serviceConfigurations: Record<string, ServiceConfig> = {
  'default': {
    serviceId: 'default',
    serviceName: 'RideQuest Default',
    primaryColor: '#3b82f6',
    maxDistance: 500,
    defaultLocation: { lat: -33.8688, lng: 151.2093 }, // Sydney
    defaultZoom: 10,
    contactInfo: {
      phone: '+61 400 000 000',
      email: 'anthony@etechconsulting.com.au',
    },
    timezone: 'Australia/Sydney',
    communicationMethods: ['voice', 'sms', 'email', 'whatsapp'],
    attributes: {
      region: 'Default',
      showContactInfo: true,
      vehicleTypes: 'sedan,suv,luxury',
      currencySymbol: '$',
      paymentTypes: 'cash,credit card,account',
      showComfortLevels: true,
      defaultCommunicationMethod: 'email',
      enableGlobalSearch: true
    }
  },
  'fiji': {
    serviceId: 'fiji',
    serviceName: 'ON Time Taxis & Tours',
    primaryColor: '#f59e0b', // amber-500
    maxDistance: 200,
    defaultLocation: { lat: -17.7134, lng: 178.0650 }, // Fiji (coordinates for Suva, Fiji)
    defaultZoom: 10, // Closer view for a smaller area
    contactInfo: {
      phone: '+679 7503541‬ or 9313429',
      email: 'singhneerajsandeep@gmail.com',
    },
    timezone: 'Pacific/Fiji',
    communicationMethods: ['email'],    attributes: {
      region: 'Fiji',
      showContactInfo: true,
      vehicleTypes: 'sedan,suv,luxury',
      currencySymbol: '$',
      paymentTypes: 'cash,credit card, invoice',
      showComfortLevels: false,
      defaultCommunicationMethod: 'email',
      enableGlobalSearch: true
    }
  },
  'la': {
    serviceId: 'la',
    serviceName: 'LA Express',
    primaryColor: '#ef4444', // red-500
    maxDistance: 150,
    defaultLocation: { lat: 34.0522, lng: -118.2437 }, // Los Angeles
    defaultZoom: 10, // Wider view for a larger metropolitan area
    contactInfo: {
      phone: '+1-310-555-6789',
      email: 'la@ridequest.com',
    },    timezone: 'America/Los_Angeles',
    communicationMethods: ['voice', 'sms', 'email'],
    attributes: {
      region: 'Los Angeles',
      showContactInfo: true,
      vehicleTypes: 'sedan,suv,luxury,electric',
      currencySymbol: '$',
      paymentTypes: 'credit card,account,apple pay,google pay',
      showComfortLevels: true,
      defaultCommunicationMethod: 'Email',
      enableGlobalSearch: false
    }
  },
  'london': {
    serviceId: 'london',
    serviceName: 'London Transport',
    primaryColor: '#00AEEF', // Example: Blue for London
    maxDistance: 150, // Max distance in km for London
    defaultLocation: { lat: 51.5074, lng: -0.1278 }, // London
    defaultZoom: 11,
    contactInfo: {
      phone: '+44 20 7123 4567',
      email: 'info@londontransport.co.uk',
    },    timezone: 'Europe/London',
    communicationMethods: ['voice', 'sms', 'email'],
    attributes: {
      region: 'Greater London',
      vehicleTypes: ['Black Cab', 'Minicab', 'Executive'],
      paymentTypes: 'Credit Card, Contactless, Oyster',
      showComfortLevels: false,
      defaultCommunicationMethod: 'Email'
    }
  },
  'paris': {
    serviceId: 'paris',
    serviceName: 'Paris Chauffeur',
    primaryColor: '#8b5cf6', // violet-500
    maxDistance: 120,
    defaultLocation: { lat: 48.8566, lng: 2.3522 }, // Paris
    defaultZoom: 12, // Moderate zoom for central Paris
    contactInfo: {
      phone: '+33-1-5555-1234',
      email: 'paris@ridequest.com',
    },    timezone: 'Europe/Paris',
    communicationMethods: ['voice', 'sms', 'email'],
    attributes: {
      region: 'Paris',
      showContactInfo: true,
      vehicleTypes: 'sedan,luxury,electric',
      currencySymbol: '€',
      paymentTypes: 'cash,credit card,carte bancaire',
      showComfortLevels: true,
      defaultCommunicationMethod: 'Email',
      enableGlobalSearch: false
    }
  },
  'nyc': {
    serviceId: 'nyc',
    serviceName: "NYC Rides",
    primaryColor: "#f7b731", // Example: Yellow for NYC taxis
    defaultLocation: { lat: 40.7128, lng: -74.0060 }, // New York City
    defaultZoom: 12,
    maxDistance: 100, // Max distance in km for NYC
    contactInfo: {
      phone: "1-800-NYCRIDE",
      email: "support@nycrides.com"
    },    timezone: "America/New_York",
    communicationMethods: ['voice', 'sms', 'email'],
    attributes: {
      region: "New York City",
      vehicleTypes: ["Sedan", "SUV", "Van"],
      paymentTypes: "Credit Card, Apple Pay, Cash",
      showComfortLevels: true,
      defaultCommunicationMethod: "SMS"
    }
  },
  'londonTransport': {
    serviceId: 'londonTransport',
    serviceName: "London Transport",
    primaryColor: "#00AEEF", // Example: Blue for London
    defaultLocation: { lat: 51.5074, lng: -0.1278 }, // London
    defaultZoom: 11,
    maxDistance: 150, // Max distance in km for London
    contactInfo: {
      phone: "+44 20 7123 4567",
      email: "info@londontransport.co.uk"
    },    timezone: "Europe/London",
    communicationMethods: ['voice', 'sms', 'email'],
    attributes: {
      region: "Greater London",
      vehicleTypes: ["Black Cab", "Minicab", "Executive"],
      paymentTypes: "Credit Card, Contactless, Oyster",
      showComfortLevels: false,
      defaultCommunicationMethod: "Email"
    }
  }
};

// Default configuration if no key is provided or key is invalid
export const defaultConfig: ServiceConfig = {
  serviceId: "default",
  serviceName: "RideQuest",
  primaryColor: "#3b82f6", // Default blue
  defaultLocation: { lat: 34.0522, lng: -118.2437 }, // Los Angeles (example default)
  defaultZoom: 10,
  maxDistance: 500, // Default max distance
  contactInfo: {
    phone: "1-888-RIDE-QST",
    email: "contact@ridequest.com"
  },  timezone: "America/Los_Angeles",
  communicationMethods: ['voice', 'sms', 'email', 'whatsapp'],
  attributes: {
    region: "Default Region",
    vehicleTypes: ["Standard", "Premium"],
    paymentTypes: "Credit Card, PayPal",
    showComfortLevels: true,
    defaultCommunicationMethod: "Email"
  }
};

// Get configuration by service key, with fallback to default
export function getServiceConfig(key: string | null): ServiceConfig {
  if (key && serviceConfigurations[key]) {
    const config = serviceConfigurations[key];
    
    // Ensure timezone is part of the main config, not just attributes
    if (config.attributes?.timezone && !config.timezone) {
      config.timezone = String(config.attributes.timezone);
    }
    
    // Ensure communicationMethods is part of the main config, not just attributes
    // This provides backward compatibility for older config formats
    if (config.attributes?.communicationMethods && !config.communicationMethods) {
      const methods = String(config.attributes.communicationMethods);
      config.communicationMethods = methods.split(',').map(method => method.trim());
      
      // Remove from attributes to prevent duplication
      delete config.attributes.communicationMethods;
    }
    
    return config;
  }
  
  // Ensure timezone is part of the default config
  if (defaultConfig.attributes?.timezone && !defaultConfig.timezone) {
    defaultConfig.timezone = String(defaultConfig.attributes.timezone);
  }
  
  // Ensure communicationMethods is part of the default config
  if (defaultConfig.attributes?.communicationMethods && !defaultConfig.communicationMethods) {
    const methods = String(defaultConfig.attributes.communicationMethods);
    defaultConfig.communicationMethods = methods.split(',').map(method => method.trim());
    
    // Remove from attributes to prevent duplication
    delete defaultConfig.attributes.communicationMethods;
  }
  
  return defaultConfig;
}
